<template>
	<div class="settings-container">
		<!-- Page header -->
		<PageHeader title="Settings" />

		<!-- Content wrapper -->
		<div class="settings">
			<!-- Profile Information -->
			<div class="card-wrapper">
				<Card :header="header" :button-action="toggleEditing" :data-to-show="dataToShow" />
			</div>
		</div>

		<!-- Edit Modal -->
		<EditModal
			:editing="editing"
			:toggle-editing="toggleEditing"
			:header="header"
			:data-to-edit="dataToEdit"
			:submit-action="updateProfileInfo"
		/>

		<!-- The dialog box confirms password resets -->
		<Dialog :is-dialog-open="showResetDialog" :on-close="toggleResetDialog">
			<div class="dialog-message">
				<div class="dialog-message__body">
					<p class="dialog-message__body--bold">{{ resetMessage }}</p>
				</div>

				<div v-if="resetStatus !== 'submitted'" class="dialog-message__buttons">
					<button
						@click.prevent="toggleResetDialog"
						:disabled="resetStatus === 'submitting'"
						class="dialog-message__buttons--back"
						style="margin-right:auto;"
					>
						<span>No way!</span>
					</button>
					<button
						@click.prevent="resetPassword"
						:disabled="resetStatus === 'submitting'"
						class="dialog-message__buttons--ok"
						style="margin-right:auto;"
					>
						<span>Yes!</span>
					</button>
				</div>
				<div v-else class="dialog-message__buttons">
					<button @click.prevent="toggleResetDialog" class="dialog-message__buttons--ok" style="margin:0 auto;">
						<span>OK</span>
					</button>
				</div>
			</div>
		</Dialog>

		<!-- The dialog box shows the user whether their email update was successful -->
		<Dialog :is-dialog-open="showStatusDialog" :on-close="toggleStatusDialog">
			<div class="dialog-message">
				<div class="dialog-message__body">
					<p class="dialog-message__body--bold">{{ statusMessage }}</p>
				</div>

				<div class="dialog-message__buttons">
					<button @click.prevent="toggleStatusDialog" class="dialog-message__buttons--ok" style="margin:0 auto;">
						<span>OK</span>
					</button>
				</div>
			</div>
		</Dialog>
	</div>
</template>

<script>
import * as eCodes from '@/utilities/errorCodes';
import { SERVER_ERROR, APOLOGIZE, TRY_AND_CONTACT, ERROR_CONTACT } from '@/utilities/errorResponses';
import CREATE_PASSWORD_RESET from '@/graphql/mutations/createPasswordReset.gql';
import logger from 'client-logger';

export default {
	data() {
		return {
			editing: false,
			header: 'Profile Information',
			showResetDialog: false,
			resetStatus: '',
			resetMessage: 'Are you sure you would like to reset your password?',
			showStatusDialog: false,
			statusMessage: ''
		};
	},
	computed: {
		currentUser() {
			return this.$store.User.currentUser;
		},
		dataToShow() {
			return {
				firstName: {
					label: 'First name',
					value: this.currentUser.firstName
				},
				lastName: {
					label: 'Last name',
					value: this.currentUser.lastName
				},
				email: {
					label: 'Email',
					value: this.currentUser.email
				},
				passwordReset: {
					label: '',
					value: 'Reset Password',
					link: '#',
					action: this.toggleResetDialog.bind(this)
				}
			};
		},
		dataToEdit() {
			return {
				firstName: {
					label: 'First name',
					accessor: 'firstName',
					type: 'text',
					value: this.currentUser.firstName,
					required: true
				},
				lastName: {
					label: 'Last name',
					accessor: 'lastName',
					type: 'text',
					value: this.currentUser.lastName,
					required: true
				},
				email: {
					label: 'Email',
					accessor: 'email',
					type: 'email',
					value: this.currentUser.email
				}
			};
		}
	},
	methods: {
		toggleEditing() {
			this.editing = !this.editing;
		},
		async updateProfileInfo(userData) {
			// Only send data that was actually edited
			// We can check against currentUser from the global store
			const variables = {};
			const toCheck = ['firstName', 'lastName', 'email'];

			for (const property of toCheck) {
				if (userData[property].value !== this.currentUser[property]) {
					variables[property] = userData[property].value;
				}
			}

			// Return data to EditModal
			const editModalData = {
				success: false,
				message: ''
			};

			// If nothing was edited, just show success message, otherwise actually update data
			if (Object.keys(variables).length === 0) {
				editModalData.success = true;
			} else {
				// Mark this user as updating himself!
				this.$set(this.$store.User, 'selfUpdate', true);

				// Make call to update
				const serverResponse = await this.$store.User.updateUser(this.currentUser.id, variables);
				if (serverResponse.data) {
					// Successful
					editModalData.success = true;

					// Specify an action to refresh the page after the dialog is closed
					editModalData.action = () => {
						window.location.reload(false);
					};

					// Add a tailored message if they tried updating their email address
					if (variables.email) {
						editModalData.message = `Successfully updated ${this.header.toLowerCase()}! Your updated email address will appear here once you have verified it in the next 24 hours by clicking on the link in the email we just sent you.`;
					}
				} else {
					// Hmm...didn't work. Be sure to revert the selfUpdate mark.
					this.$set(this.$store.User, 'selfUpdate', false);

					switch (serverResponse.error.code) {
						case eCodes.NOT_FOUND:
							editModalData.message = 'We were unable to find that user.';
							break;

						default:
							editModalData.message = `${SERVER_ERROR} ${APOLOGIZE} ${TRY_AND_CONTACT}`;
					}
				}
			}
			return editModalData;
		},
		toggleResetDialog() {
			this.showResetDialog = !this.showResetDialog;

			// If we've just closed the dialog box after submitting a reset, we need to reset the UI, too
			if (this.resetStatus) {
				this.resetStatus = '';
				this.resetMessage = 'Are you sure you would like to reset your password?';
			}
		},
		async resetPassword() {
			// Disable the button
			this.resetStatus = 'submitting';

			// Define the mutation
			const mutation = {
				mutation: CREATE_PASSWORD_RESET,
				variables: {
					email: this.$store.User.currentUser.email
				}
			};

			// Send the mutation
			try {
				await this.$apollo.mutate(mutation);
			} catch (e) {
				logger.error({ error: e }, 'Failed to create password reset');
			}

			// Show them that all went well
			this.resetStatus = 'submitted';
			this.resetMessage =
				'Success! You should have received an email from us in your inbox. Please follow the instructions in the next 12 hours to reset your password.';
		},
		toggleStatusDialog() {
			this.showStatusDialog = !this.showStatusDialog;

			// Remove the query params upon closing
			if (!this.showStatusDialog) {
				// Clear the router query
				this.$router.replace({
					...this.$router.currentRoute,
					query: {}
				});
			}
		}
	},
	components: {
		PageHeader: () => import(/* webpackChunkName: "PageHeader" */ '@/components/shared/PageHeader'),
		EditModal: () => import(/* webpackChunkName: "EditModal" */ '@/components/shared/EditModal'),
		Card: () => import(/* webpackChunkName: "Card" */ '@/components/shared/Card'),
		Dialog: () => import(/* webpackChunkName: "Dialog" */ '@/components/shared/Dialog')
	},
	created() {
		// In the rare occasion in which the user landed on this page because they clicked on an email verification link, we should show them a message
		const queryParams = this.$route.query;

		if (queryParams.update && queryParams.status) {
			const updateType = queryParams.update;

			if (queryParams.status === 'success') {
				// Show them success
				this.statusMessage = `Success! Your ${updateType} has been verified and updated!`;
			} else {
				// Define our error reason and show it
				let reason;
				switch (queryParams.reason) {
					case 'expired':
						reason = `your ${updateType} verification link has expired. Please submit another update request and try again. ${ERROR_CONTACT}`;
						break;
					case 'internal':
					default:
						reason = `we encountered a server error trying to update your ${updateType}. ${APOLOGIZE} ${TRY_AND_CONTACT}`;
				}
				this.statusMessage = `Oops. We could not update your ${updateType} because ${reason}`;
			}

			this.toggleStatusDialog();
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/brand-container.scss';
@import '@/styles/mixins/brand-fonts.scss';
@import '@/styles/mixins/brand-link.scss';
@import '@/styles/mixins/brand-form.scss';
@import '@/styles/mixins/card.scss';

.settings-container {
	@include Brand-Container(md);
	padding-bottom: 20px;

	@include media-breakpoint-up(md) {
		padding-bottom: 60px;
	}

	.settings {
		display: flex;
		flex-direction: column;

		@include media-breakpoint-up(md) {
			flex-direction: row;
			justify-content: center;
		}

		.card-wrapper {
			@include media-breakpoint-up(md) {
				min-width: 700px;
				margin: 0 36px;
			}
		}
	}
}
</style>
